import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const ICON_OPTIONS = [
    'DocumentCheckIcon',
    'UsersIcon',
    'MapPinIcon',
    'BanknotesIcon',
    'ShieldCheckIcon',
    'ClockIcon',
    'BuildingOfficeIcon',
    'CheckCircleIcon',
    'HomeIcon'
];

const AdminScorecardWhyChoosePage = () => {
    const [reasons, setReasons] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Why Homeowners Choose Aham Housing Finance',
        image_url: '',
        image_title: 'Your Dream Home, Made Reality',
        image_subtitle: 'Experience a hassle-free loan process tailored for you.'
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingReason, setEditingReason] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, reasonId: null });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        icon_name: 'DocumentCheckIcon',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [reasonsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/eligibility-scorecard/why-choose/reasons'),
                axios.get('/api/admin/eligibility-scorecard/why-choose/settings')
            ]);

            if (reasonsRes.data.success) {
                setReasons(reasonsRes.data.data);
            }
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings({
                    section_title: settingsRes.data.data.section_title || '',
                    image_url: settingsRes.data.data.image_url || '',
                    image_title: settingsRes.data.data.image_title || '',
                    image_subtitle: settingsRes.data.data.image_subtitle || ''
                });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load scorecard why choose data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            const data = new FormData();
            data.append('section_title', settings.section_title);
            data.append('image_title', settings.image_title);
            data.append('image_subtitle', settings.image_subtitle);
            if (selectedImage) {
                data.append('image', selectedImage);
            } else {
                data.append('image_url', settings.image_url);
            }

            const response = await axios.put('/api/admin/eligibility-scorecard/why-choose/settings', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast.success('Settings updated successfully');
                setSettings(prev => ({
                    ...prev,
                    image_url: response.data.data.image_url
                }));
                setSelectedImage(null);
                setImagePreview(null);
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleEdit = (reason) => {
        setEditingReason(reason);
        setIsAdding(false);
        setFormData({
            title: reason.title,
            description: reason.description,
            icon_name: reason.icon_name,
            display_order: reason.display_order,
            is_active: reason.is_active === 1 || reason.is_active === true
        });
    };

    const handleCancel = () => {
        setEditingReason(null);
        setIsAdding(false);
        setFormData({
            title: '',
            description: '',
            icon_name: 'DocumentCheckIcon',
            display_order: 0,
            is_active: true
        });
    };

    const handleSaveReason = async () => {
        try {
            if (!formData.title || !formData.description) {
                toast.error('Please enter both title and description');
                return;
            }

            if (isAdding) {
                const response = await axios.post('/api/admin/eligibility-scorecard/why-choose/reasons', formData);
                if (response.data.success) {
                    toast.success('Reason added successfully');
                    fetchData();
                    handleCancel();
                }
            } else {
                const response = await axios.put(`/api/admin/eligibility-scorecard/why-choose/reasons/${editingReason.id}`, formData);
                if (response.data.success) {
                    toast.success('Reason updated successfully');
                    fetchData();
                    handleCancel();
                }
            }
        } catch (error) {
            console.error('Error saving reason:', error);
            toast.error('Failed to save reason');
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await axios.delete(`/api/admin/eligibility-scorecard/why-choose/reasons/${deleteConfirm.reasonId}`);
            if (response.data.success) {
                toast.success('Reason deleted successfully');
                fetchData();
                setDeleteConfirm({ isOpen: false, reasonId: null });
            }
        } catch (error) {
            console.error('Error deleting reason:', error);
            toast.error('Failed to delete reason');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Why Choose Section</h1>
                    <p className="text-slate-500 mt-1">Manage the "Why Homeowners Choose" section on the Scorecard page.</p>
                </div>
                {!isAdding && !editingReason && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="px-4 py-2 bg-[#283079] text-white rounded-lg hover:bg-[#1f2559] flex items-center gap-2 font-semibold shadow-md"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Reason
                    </button>
                )}
            </div>

            {/* Section Settings */}
            {!isAdding && !editingReason && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Section Title
                                </label>
                                <input
                                    type="text"
                                    value={settings.section_title}
                                    onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Image Overlay Title
                                </label>
                                <input
                                    type="text"
                                    value={settings.image_title}
                                    onChange={(e) => setSettings({ ...settings, image_title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Image Overlay Subtitle
                                </label>
                                <input
                                    type="text"
                                    value={settings.image_subtitle}
                                    onChange={(e) => setSettings({ ...settings, image_subtitle: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Image
                            </label>
                            <div className="flex flex-col gap-3">
                                {imagePreview || settings.image_url ? (
                                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] max-w-[240px] border border-slate-200">
                                        <img
                                            src={imagePreview || (settings.image_url.startsWith('http') ? settings.image_url : `/${settings.image_url}`)}
                                            alt="Section Image"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="rounded-2xl max-w-[240px] aspect-[4/3] bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                                        No Image
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <input
                                        type="file"
                                        accept=".png, .webp"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const allowedTypes = ['image/png', 'image/webp'];
                                                if (!allowedTypes.includes(file.type)) {
                                                    toast.error('Only PNG and WebP image formats are allowed!');
                                                    e.target.value = '';
                                                    return;
                                                }

                                                // Validate image dimensions
                                                const img = new Image();
                                                img.src = URL.createObjectURL(file);
                                                img.onload = () => {
                                                    if (img.width !== 900 || img.height !== 600) {
                                                        toast.error(`Image must be exactly 900 x 600 pixels. Got: ${img.width} x ${img.height} pixels.`);
                                                        e.target.value = '';
                                                        setSelectedImage(null);
                                                        setImagePreview(null);
                                                        return;
                                                    }
                                                    setSelectedImage(file);
                                                    setImagePreview(img.src);
                                                };
                                            }
                                        }}
                                        className="text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-slate-50 file:text-[#283079] hover:file:bg-slate-100"
                                    />
                                    <p className="text-xs text-slate-400 font-medium">Allowed formats: PNG, WebP only. Dimensions: exactly 500 x 600 px</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleSettingsSave}
                            disabled={savingSettings}
                            className="bg-[#283079] hover:bg-[#1f2559] text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                        >
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {/* Reason Form */}
            {(isAdding || editingReason) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <h3 className="text-xl font-bold mb-6 text-slate-800 border-b pb-4">{isAdding ? 'Add New Reason' : 'Edit Reason'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Title <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. Flexible assessment"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Description <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. not just a payslip check."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                            <select
                                value={formData.icon_name}
                                onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            >
                                {ICON_OPTIONS.map(icon => (
                                    <option key={icon} value={icon}>{icon}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                            />
                        </div>
                    </div>

                    <div className="mb-6 flex items-center gap-3 bg-slate-50 p-4 rounded-xl">
                        <div className="flex items-center h-5">
                            <input
                                id="is_active"
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="w-5 h-5 text-[#283079] border-slate-300 rounded focus:ring-[#283079]"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="is_active" className="text-sm font-semibold text-slate-800">
                                Active Status
                            </label>
                            <p className="text-xs text-slate-500">
                                When active, this reason will be visible on the public scorecard page.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-8 justify-end">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveReason}
                            className="px-6 py-2.5 bg-[#283079] text-white font-semibold rounded-xl hover:bg-[#1f2559] flex items-center gap-2 shadow-md"
                        >
                            {isAdding ? 'Save Reason' : 'Update Reason'}
                        </button>
                    </div>
                </div>
            )}

            {/* Reasons Table */}
            {!isAdding && !editingReason && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Order</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Status</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Icon</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Title</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Description</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">Loading...</td>
                                    </tr>
                                ) : reasons.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">No reasons found. Add one above.</td>
                                    </tr>
                                ) : (
                                    reasons.map((reason) => (
                                        <tr key={reason.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                            <td className="p-4 text-slate-500 text-sm font-medium">#{reason.display_order}</td>
                                            <td className="p-4">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${reason.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                                    {reason.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-600 text-xs font-mono">{reason.icon_name}</td>
                                            <td className="p-4 text-slate-800 text-sm font-semibold">{reason.title}</td>
                                            <td className="p-4 text-slate-600 text-sm">{reason.description}</td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(reason)}
                                                        className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-[#283079] transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm({ isOpen: true, reasonId: reason.id })}
                                                        className="p-1.5 hover:bg-red-100 rounded text-slate-500 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, reasonId: null })}
                onConfirm={confirmDelete}
                title="Delete Reason"
                message="Are you sure you want to delete this reason? This action cannot be undone."
            />
        </div>
    );
};

export default AdminScorecardWhyChoosePage;
